import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReportsGate from '../components/ReportsGate';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function formatKg(value) {
  const n = Number(value) || 0;
  if (n >= 1000) return `${(n / 1000).toLocaleString('en-AU', { maximumFractionDigits: 2 })} t CO₂e`;
  if (n >= 10) return `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} kg CO₂e`;
  return `${n.toLocaleString('en-AU', { maximumFractionDigits: 2 })} kg CO₂e`;
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function CustomerFootprintReportsInner() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API_BASE_URL}/reports/customer-footprint`);
        if (!cancelled) setReports(data?.data || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-3" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <Link to="/reports" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
            &larr; Reports
          </Link>
          <h1 className="fw-bold mb-1 mt-1">Customer Footprint Reports</h1>
          <p className="text-muted mb-0">History of reports sent to your customers.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/reports/customer-footprint/new')}
        >
          + New report
        </button>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : reports.length === 0 ? (
        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="card-body p-5 text-center">
            <div style={{ fontSize: 40 }}>📨</div>
            <h2 className="h5 fw-bold mt-3 mb-2">No reports yet</h2>
            <p className="text-muted mb-3">
              Send your first carbon footprint report to a customer.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/reports/customer-footprint/new')}
            >
              + New report
            </button>
          </div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm" style={{ borderRadius: 12 }}>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="ps-3">Customer</th>
                  <th>Sent</th>
                  <th>Products</th>
                  <th className="text-end">Total</th>
                  <th className="text-end pe-3"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/reports/customer-footprint/${r.id}`)}
                  >
                    <td className="ps-3">
                      <div className="fw-semibold">{r.customer_name}</div>
                      <div className="text-muted" style={{ fontSize: 13 }}>{r.customer_email}</div>
                    </td>
                    <td>{formatDate(r.sent_at || r.created_at)}</td>
                    <td>{r.product_count}</td>
                    <td className="text-end fw-semibold">{formatKg(r.total_emissions_kg)}</td>
                    <td className="text-end pe-3">
                      <span className="text-primary" style={{ fontSize: 13 }}>View &rarr;</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomerFootprintReports() {
  return (
    <ReportsGate>
      <CustomerFootprintReportsInner />
    </ReportsGate>
  );
}
